import { format, formatDistanceToNowStrict, parseISO, isValid } from 'date-fns';

const DATE_FMT      = 'MMM d, yyyy';
const DATETIME_FMT  = 'MMM d, yyyy · HH:mm';

function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) return isValid(value) ? value : null;
  const d = typeof value === 'string' ? parseISO(value) : new Date(value);
  return isValid(d) ? d : null;
}

export function fmtDate(value, fallback = '—') {
  const d = toDate(value);
  return d ? format(d, DATE_FMT) : fallback;
}

export function fmtDateTime(value, fallback = '—') {
  const d = toDate(value);
  return d ? format(d, DATETIME_FMT) : fallback;
}

export function fmtRelative(value, fallback = '—') {
  const d = toDate(value);
  if (!d) return fallback;
  return formatDistanceToNowStrict(d, { addSuffix: true });
}

export function fmtPriorityIndex(pi) {
  const n = Number(pi);
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(2);
}

export function fmtPercent(decimal, digits = 0) {
  const n = Number(decimal);
  if (!Number.isFinite(n)) return '—';
  return `${(n * 100).toFixed(digits)}%`;
}

export function fmtCurrency(value, currency = 'USD') {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 2 }).format(n);
}

export function fmtNumber(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('en-US').format(n);
}

/**
 * Map a status string into a tailwind palette: { bg, text, ring }.
 * Unknown values fall back to neutral slate.
 */
const STATUS_PALETTES = {
  // Generic positive
  active:        { bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-200' },
  verified:      { bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-200' },
  confirmed:     { bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-200' },
  resolved:      { bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-200' },
  closed:        { bg: 'bg-slate-100',  text: 'text-slate-700',   ring: 'ring-slate-200' },

  // Generic neutral / pending
  queued:        { bg: 'bg-sky-50',     text: 'text-sky-700',     ring: 'ring-sky-200' },
  routed:        { bg: 'bg-sky-50',     text: 'text-sky-700',     ring: 'ring-sky-200' },
  pending:       { bg: 'bg-sky-50',     text: 'text-sky-700',     ring: 'ring-sky-200' },
  ingested:      { bg: 'bg-sky-50',     text: 'text-sky-700',     ring: 'ring-sky-200' },
  open:          { bg: 'bg-sky-50',     text: 'text-sky-700',     ring: 'ring-sky-200' },

  // Review states
  under_review:  { bg: 'bg-indigo-50',  text: 'text-indigo-700',  ring: 'ring-indigo-200' },
  challenged:    { bg: 'bg-amber-50',   text: 'text-amber-700',   ring: 'ring-amber-200' },

  // Negative
  failed:        { bg: 'bg-rose-50',    text: 'text-rose-700',    ring: 'ring-rose-200' },
  revoked:       { bg: 'bg-rose-50',    text: 'text-rose-700',    ring: 'ring-rose-200' },
  compromised:   { bg: 'bg-rose-50',    text: 'text-rose-700',    ring: 'ring-rose-200' },
  false_positive:{ bg: 'bg-slate-100',  text: 'text-slate-700',   ring: 'ring-slate-200' },
  dismissed:     { bg: 'bg-slate-100',  text: 'text-slate-700',   ring: 'ring-slate-200' },
  expired:       { bg: 'bg-slate-100',  text: 'text-slate-700',   ring: 'ring-slate-200' },
  inactive:      { bg: 'bg-slate-100',  text: 'text-slate-600',   ring: 'ring-slate-200' },

  // VERIS dual-control lifecycle (App 4 §12.3). `first_approved` is amber
  // because it is the state that needs a human — one signature short.
  first_approved:{ bg: 'bg-amber-50',   text: 'text-amber-700',   ring: 'ring-amber-200' },
  authorized:    { bg: 'bg-indigo-50',  text: 'text-indigo-700',  ring: 'ring-indigo-200' },
  executed:      { bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-200' },
  rejected:      { bg: 'bg-rose-50',    text: 'text-rose-700',    ring: 'ring-rose-200' },

  // VERIS eligibility states (CTO §8.1)
  eligible:              { bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-200' },
  restored:              { bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-200' },
  pending_review:        { bg: 'bg-sky-50',     text: 'text-sky-700',     ring: 'ring-sky-200' },
  ineligible_temporary:  { bg: 'bg-amber-50',   text: 'text-amber-700',   ring: 'ring-amber-200' },
  ineligible_permanent:  { bg: 'bg-rose-50',    text: 'text-rose-700',    ring: 'ring-rose-200' },

  // Propagation + manifest delivery
  delivered:     { bg: 'bg-sky-50',     text: 'text-sky-700',     ring: 'ring-sky-200' },
  acknowledged:  { bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-200' },

  // Vision — threat bundles + live-stream consent
  detected:      { bg: 'bg-amber-50',   text: 'text-amber-700',   ring: 'ring-amber-200' },
  requested:     { bg: 'bg-amber-50',   text: 'text-amber-700',   ring: 'ring-amber-200' },
  granted:       { bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-200' },
  denied:        { bg: 'bg-slate-100',  text: 'text-slate-700',   ring: 'ring-slate-200' },

  // Vision — device consent lifecycle (cameras / sensors / vehicles)
  paused:        { bg: 'bg-amber-50',   text: 'text-amber-700',   ring: 'ring-amber-200' },
  withdrawn:     { bg: 'bg-slate-100',  text: 'text-slate-700',   ring: 'ring-slate-200' },
  deactivated:   { bg: 'bg-slate-100',  text: 'text-slate-700',   ring: 'ring-slate-200' },

  // RERR field units
  available:     { bg: 'bg-emerald-50', text: 'text-emerald-700', ring: 'ring-emerald-200' },
  dispatched:    { bg: 'bg-sky-50',     text: 'text-sky-700',     ring: 'ring-sky-200' },
  unavailable:   { bg: 'bg-slate-100',  text: 'text-slate-700',   ring: 'ring-slate-200' },
  off_duty:      { bg: 'bg-slate-100',  text: 'text-slate-600',   ring: 'ring-slate-200' },
};

export function statusPalette(status) {
  if (!status) return STATUS_PALETTES.pending;
  const key = String(status).toLowerCase();
  return STATUS_PALETTES[key] || { bg: 'bg-slate-100', text: 'text-slate-700', ring: 'ring-slate-200' };
}

export function fmtRoleLabel(role) {
  if (!role) return '';
  return role
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function initialsOf(name) {
  if (!name) return '·';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0].toUpperCase())
    .join('') || '·';
}
